// Pantalla del paso 02 para cada motivo por el que la verificación no avanzó (`kycFailure.code`).
import { ApiError } from '@/api/errors';
import type { KycFailure } from './types';

/**
 * Qué puede hacer el vendedor ante cada motivo.
 *
 * - `RETRY` — volver a armar la selfie en la modalidad que elija.
 * - `SEND_LINK` — mandar un link nuevo (o reintentar en la tablet).
 * - `FINISH` — la sesión ya está cerrada: solo se navega a una venta nueva.
 * - `FINISH_WITH_CANCEL` — la sesión sigue abierta: se cancela con el code como motivo.
 * - `WAIT` — el silo reintenta solo; el poll sigue.
 * - `NONE` — sin acción propia; quedan las salidas del pie.
 */
export type KycFailureAction =
    | 'RETRY'
    | 'SEND_LINK'
    | 'FINISH'
    | 'FINISH_WITH_CANCEL'
    | 'WAIT'
    | 'NONE';

export interface KycFailureCopy {
    title: string;
    text: string;
    tone: 'danger' | 'warning' | 'info';
    action: KycFailureAction;
}

export const KYC_FAILURE_COPY: Record<KycFailure, KycFailureCopy> = {
    FACEMATCH_NO_MATCH: {
        title: 'La selfie no coincide',
        text: 'No coincide con su foto registrada. Te queda 1 intento.',
        tone: 'warning',
        action: 'RETRY',
    },
    FACEMATCH_REJECTED: {
        title: 'Venta rechazada',
        text: 'No pudimos confirmar su identidad.',
        tone: 'danger',
        action: 'FINISH',
    },
    FACEMATCH_EXPIRED: {
        title: 'La verificación venció',
        text: 'El link venció. Envía uno nuevo.',
        tone: 'warning',
        action: 'SEND_LINK',
    },
    FACEMATCH_PROVIDER_FAILED: {
        title: 'No se pudo verificar',
        text: 'Hubo un problema al verificar. Intenta de nuevo.',
        tone: 'warning',
        action: 'RETRY',
    },
    FACEMATCH_PREPARE_UNAVAILABLE: {
        title: 'No se pudo preparar la verificación',
        text: 'No pudimos preparar la verificación. Intenta de nuevo.',
        tone: 'warning',
        action: 'RETRY',
    },
    FACEMATCH_SETUP_FAILED: {
        title: 'No se pudo preparar la verificación',
        text: 'No pudimos preparar la verificación. Contacta a soporte.',
        tone: 'danger',
        action: 'FINISH_WITH_CANCEL',
    },
    FIADO_IDENTITY_INCOMPLETE: {
        title: 'No se puede continuar con esta cuenta',
        text: 'No pudimos usar los datos de su cuenta. Contacta a soporte.',
        tone: 'danger',
        action: 'FINISH_WITH_CANCEL',
    },
    FIADO_CURP_MISMATCH: {
        title: 'Los datos no coinciden',
        text: 'El CURP de su cuenta Pago Confiado no coincide con su registro en SureKeep. Contacta a soporte para corregirlo.',
        tone: 'danger',
        action: 'FINISH',
    },
    REGISTRATION_PENDING: {
        title: 'Registrando al cliente',
        text: 'Estamos registrando al cliente. Esto puede tardar unos segundos.',
        tone: 'info',
        action: 'WAIT',
    },
    UNKNOWN: {
        title: 'No se pudo completar el registro',
        text: 'Algo falló al registrar al cliente. Si sigue igual, contacta a soporte.',
        tone: 'warning',
        action: 'NONE',
    },
};

/** Codes del 502 de `kyc/prepare` que tienen pantalla propia. */
const PREPARE_FAILURE_CODES: ReadonlySet<string> = new Set<KycFailure>([
    'FACEMATCH_PREPARE_UNAVAILABLE',
    'FACEMATCH_SETUP_FAILED',
]);

/** El motivo que trae el error de `kyc/prepare`, si tiene pantalla propia. */
export function prepareFailureOf(error: unknown): KycFailure | null {
    if (error instanceof ApiError && PREPARE_FAILURE_CODES.has(error.code)) {
        return error.code as KycFailure;
    }
    return null;
}

/**
 * ¿El silo rechazó la acción porque la sesión ya no admite ese paso? Ahí se relee `kyc/status` y se
 * decide por su `kycFailure.code`. `ADDRESS_REQUIRED` tiene su propio manejo.
 */
export function isSessionConflict(error: unknown): boolean {
    return (
        error instanceof ApiError && error.httpStatus === 409 && error.code !== 'ADDRESS_REQUIRED'
    );
}
